import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const GUIDES = [
  {
    icon: 'ri-calendar-event-line',
    color: 'bg-teal-50 text-teal-600',
    title: 'Creating Your First Event',
    time: '5 min read',
    steps: [
      'Go to Dashboard → Create Event',
      'Fill in your event title, description, category, and date',
      'Set your ticket price (or leave at $0 for free)',
      'Upload a high-quality cover image (1200×630px recommended)',
      'Add your location or mark it as Online',
      'Hit Publish — your event is live!',
    ],
  },
  {
    icon: 'ri-user-add-line',
    color: 'bg-amber-50 text-amber-600',
    title: 'Using the Social Follow Gate',
    time: '3 min read',
    steps: [
      'In the event creation form, scroll to "Social Media"',
      'Add up to 2 social accounts (Starter) or 5 (Growth)',
      'Attendees must follow these accounts before registering',
      'This grows your audience automatically with every ticket sale',
      'Track new followers in your social analytics',
    ],
  },
  {
    icon: 'ri-mail-send-line',
    color: 'bg-rose-50 text-rose-600',
    title: 'Email Campaigns & Reminders',
    time: '4 min read',
    steps: [
      'Available on Growth and Pro Partner plans',
      'Go to Dashboard → Event Registrations → Email',
      'Compose a custom HTML email to all registrants',
      'Schedule automated reminders (1 week, 1 day, 1 hour before)',
      'Track open rates and click-through rates',
      'Resend to non-openers with one click',
    ],
  },
  {
    icon: 'ri-store-3-line',
    color: 'bg-cyan-50 text-cyan-600',
    title: 'Setting Up Your Store',
    time: '4 min read',
    steps: [
      'Go to Dashboard → My Store',
      'Customize your store profile and choose a template',
      'Add digital or physical products with images and pricing',
      'Share your store link on social media',
      'Manage orders and send download links from the Orders tab',
    ],
  },
  {
    icon: 'ri-bar-chart-line',
    color: 'bg-green-50 text-green-600',
    title: 'Understanding Your Analytics',
    time: '3 min read',
    steps: [
      'Go to Dashboard → Earnings for revenue overview',
      'See total event revenue, store revenue, and withdrawable balance',
      'Track registrations per event in Event Registrations',
      'Monitor email campaign performance (open/click rates)',
      'Use data to optimize future event pricing and timing',
    ],
  },
  {
    icon: 'ri-bank-card-line',
    color: 'bg-gray-50 text-gray-600',
    title: 'Withdrawing Your Earnings',
    time: '2 min read',
    steps: [
      'Event earnings unlock 48 hours after your event ends',
      'Store earnings unlock 7 days after payment',
      'Go to Dashboard → Earnings → Withdraw',
      'Choose bank transfer or mobile money',
      'Withdrawals are processed within 1–3 business days',
    ],
  },
];

const TIPS = [
  { icon: 'ri-image-line', tip: 'Use a high-quality, eye-catching cover image. Events with great images get 3x more clicks.' },
  { icon: 'ri-time-line', tip: 'Publish your event at least 2 weeks in advance to maximize registrations.' },
  { icon: 'ri-price-tag-3-line', tip: 'Offer early-bird pricing to create urgency and reward early supporters.' },
  { icon: 'ri-share-line', tip: 'Share your event link across all your social channels immediately after publishing.' },
  { icon: 'ri-team-line', tip: 'Use team access (Growth/Pro) to delegate event management to co-organizers.' },
  { icon: 'ri-mail-line', tip: 'Send a reminder email 24 hours before your event — it significantly reduces no-shows.' },
];

export default function CreatorGuidePage() {
  const { user, profile } = useAuth();
  const [activeGuide, setActiveGuide] = useState<number | null>(null);
  const [userPlan, setUserPlan] = useState<string>('starter');
  const [eventsCreated, setEventsCreated] = useState(0);

  const firstName = profile?.full_name?.split(' ')[0] || user?.user_metadata?.full_name?.split(' ')[0] || null;

  useEffect(() => {
    const PAGE_TITLE = 'Creator Guide – How to Create Successful Events | EVENTSHOWCASE';
    const PAGE_DESC = 'Comprehensive guide for event organizers. Learn how to create events, grow your audience with social follow gates, run email campaigns, and maximize earnings.';
    const PAGE_URL = `${SITE_URL}/creator-guide`;
    const PAGE_IMG = 'https://public.readdy.ai/ai/img_res/a7583556ee8a06132b9923269bfe0f62.jpg';

    document.title = PAGE_TITLE;
    if (user) fetchUserContext();

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); const [a, v] = sel.replace('meta[', '').replace(']', '').split('='); el.setAttribute(a.trim(), v.replace(/"/g, '').trim()); document.head.appendChild(el); el.setAttribute('data-page-meta', 'true'); }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'article');
    setMeta('meta[property="og:image"]', 'content', PAGE_IMG);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', PAGE_TITLE);
    setMeta('meta[name="twitter:description"]', 'content', PAGE_DESC);
    setMeta('meta[name="twitter:image"]', 'content', PAGE_IMG);

    // Schema.org JSON-LD
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Creator Guide - How to Create Successful Events on EVENTSHOWCASE',
      description: 'Comprehensive guide for event organizers. Learn how to create events, grow your audience, monetize, and maximize your earnings.',
      author: {
        '@type': 'Organization',
        name: 'EVENTSHOWCASE',
      },
      publisher: {
        '@type': 'Organization',
        name: 'EVENTSHOWCASE',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/vite.svg`,
        },
      },
      datePublished: '2026-04-05',
      dateModified: '2026-04-05',
      url: `${SITE_URL}/creator-guide`,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/creator-guide`,
      },
    };

    const existing = document.getElementById('schema-creator-guide');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-creator-guide';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-creator-guide');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  const fetchUserContext = async () => {
    if (!user) return;
    const [planRes, eventsRes] = await Promise.all([
      supabase.from('subscriptions').select('plan').eq('user_id', user.id).eq('status', 'active').order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('events').select('id', { count: 'exact', head: true }).eq('organizer_id', user.id),
    ]);
    setUserPlan(planRes.data?.plan ?? 'starter');
    setEventsCreated(eventsRes.count || 0);
  };

  const isPaidPlan = userPlan === 'growth' || userPlan === 'pro_partner';

  return (
    <div className="min-h-screen bg-white">
      <Navbar forceScrolled />

      {/* Hero */}
      <section className="pt-28 pb-16 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full text-xs font-bold uppercase tracking-wide mb-5">
            <i className="ri-book-open-line"></i> Creator Resources
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            {firstName ? `${firstName}'s Creator Guide` : 'Creator Guide'}
          </h1>
          <p className="text-lg text-gray-300 max-w-xl mx-auto">
            {firstName
              ? `Everything you need to take your events to the next level, ${firstName}.`
              : 'Everything you need to create successful events, grow your audience, and maximize your earnings.'}
          </p>

          {/* Personalised status bar */}
          {user && (
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2.5">
                <i className="ri-calendar-event-line text-teal-400"></i>
                <span className="text-sm text-white">{eventsCreated} event{eventsCreated !== 1 ? 's' : ''} created</span>
              </div>
              <div className={`flex items-center gap-2 rounded-xl px-4 py-2.5 ${isPaidPlan ? 'bg-teal-500/30' : 'bg-white/10'}`}>
                <i className={`ri-vip-crown-line ${isPaidPlan ? 'text-teal-300' : 'text-gray-400'}`}></i>
                <span className="text-sm text-white capitalize">{userPlan.replace('_', ' ')} plan</span>
                {!isPaidPlan && (
                  <Link to="/subscribe" className="text-[10px] bg-teal-500 text-white px-2 py-0.5 rounded-full font-bold hover:bg-teal-400 transition-colors whitespace-nowrap">
                    Upgrade
                  </Link>
                )}
              </div>
              <Link to="/dashboard" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-2.5 transition-colors">
                <i className="ri-dashboard-line text-gray-300"></i>
                <span className="text-sm text-white">Dashboard</span>
              </Link>
            </div>
          )}

          {!user && (
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-gray-400">
              <span className="flex items-center gap-2"><i className="ri-calendar-check-line text-teal-400"></i> Event creation</span>
              <span className="flex items-center gap-2"><i className="ri-user-add-line text-teal-400"></i> Audience growth</span>
              <span className="flex items-center gap-2"><i className="ri-money-dollar-circle-line text-teal-400"></i> Monetization</span>
            </div>
          )}
        </div>
      </section>

      {/* Upgrade nudge for Starter users */}
      {user && !isPaidPlan && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-sm text-amber-800">
              <i className="ri-lock-line mr-1.5"></i>
              <strong>Email campaigns, scheduled reminders, and team access</strong> are locked on your Starter plan.
            </p>
            <Link to="/subscribe" className="text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded-lg transition-colors whitespace-nowrap">
              Unlock with Growth →
            </Link>
          </div>
        </div>
      )}

      {/* Guides */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Step-by-Step Guides</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {GUIDES.map((guide, i) => (
            <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden">
              <button
                onClick={() => setActiveGuide(activeGuide === i ? null : i)}
                className="w-full flex items-center gap-4 p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${guide.color}`}>
                  <i className={`${guide.icon} text-xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900">{guide.title}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{guide.time}</p>
                </div>
                <i className={`ri-arrow-down-s-line text-gray-400 text-xl transition-transform flex-shrink-0 ${activeGuide === i ? 'rotate-180' : ''}`}></i>
              </button>
              {activeGuide === i && (
                <div className="px-5 pb-5 border-t border-gray-50">
                  <ol className="space-y-2.5 mt-4">
                    {guide.steps.map((step, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="w-5 h-5 bg-teal-100 text-teal-700 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{j + 1}</span>
                        <span className="text-sm text-gray-600">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Pro Tips */}
      <section className="bg-gray-50 py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Pro Tips</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TIPS.map((tip, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex items-start gap-3">
                <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${tip.icon} text-teal-600`}></i>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{tip.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Ready to create your first event?</h2>
          <p className="text-gray-500 mb-7 text-sm">It takes less than 5 minutes. No credit card required.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/create-event" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-7 py-3 rounded-xl font-semibold text-sm transition-colors whitespace-nowrap">
              <i className="ri-add-line"></i> Create Event
            </Link>
            <Link to="/subscribe" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 hover:bg-gray-50 px-7 py-3 rounded-xl font-semibold text-sm transition-colors whitespace-nowrap">
              View Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
